import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CreateCommanWay, GetCommanWay, ListCommanWay, UpdateCommanWay } from "API/commonApi";
import Barcode from "react-barcode";
import Select from "react-select";
import Swal from "sweetalert2";
import UseToast from "utils/AfToast";
import moment from "moment";
import { get_file_url } from "utils/lib-utils";
import * as fuzz from "fuzzball";
import {
  CheckCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  TruckIcon,
  InboxStackIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ArrowTopRightOnSquareIcon,
  PencilSquareIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";

// Table color palettes for physical sorting tables
const TABLE_PALETTES = [
  { table: "Table 1", bg: "bg-emerald-600", text: "text-white", lightBg: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { table: "Table 2", bg: "bg-blue-600", text: "text-white", lightBg: "bg-blue-50 text-blue-800 border-blue-200" },
  { table: "Table 3", bg: "bg-purple-600", text: "text-white", lightBg: "bg-purple-50 text-purple-800 border-purple-200" },
  { table: "Table 4", bg: "bg-amber-600", text: "text-white", lightBg: "bg-amber-50 text-amber-800 border-amber-200" },
  { table: "Table 5", bg: "bg-rose-600", text: "text-white", lightBg: "bg-rose-50 text-rose-800 border-rose-200" },
  { table: "Table 6", bg: "bg-cyan-600", text: "text-white", lightBg: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  { table: "Table 7", bg: "bg-indigo-600", text: "text-white", lightBg: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  { table: "Table 8", bg: "bg-orange-600", text: "text-white", lightBg: "bg-orange-50 text-orange-800 border-orange-200" },
  { table: "Table 9", bg: "bg-teal-600", text: "text-white", lightBg: "bg-teal-50 text-teal-800 border-teal-200" },
  { table: "Table 10", bg: "bg-violet-600", text: "text-white", lightBg: "bg-violet-50 text-violet-800 border-violet-200" },
];

export interface LoadedMotherbag {
  id: any;
  code: string;
  bagData: any;
  allAwbs: any[];
  inScannedAwbNos: string[];
  shortAwbNos: string[];
}

export interface LocationGroup {
  locationKey: string;
  locationName: string;
  tag: string;
  tableTag: string;
  palette: typeof TABLE_PALETTES[0];
  awbNos: string[];
  awbs: any[];
  totalWeight: number;
  assignedAgent: { value: any; label: string } | null;
  drsGenerated: boolean;
}

/**
 * 1. Normalize common address variants, expand abbreviations, and strip phone/mobile/landlines (>6 digits)
 */
export function normalizeAddress(addr: string = ""): string {
  return (addr || "")
    .toLowerCase()
    // Remove mobile numbers, landlines, and phone labels (sequences of 7 to 12 digits, with spaces/hyphens)
    .replace(/(?:ph|tel|mob|mobile|contact|phone)?[\s.:#-]*(?:\+91[\s-]?)?[0-9]{3,5}[\s-]?[0-9]{5,8}/gi, " ")
    .replace(/(?:ph|tel|mob|mobile|contact|phone)?[\s.:#-]*(?:\+91[\s-]?)?[0-9]{7,12}/gi, " ")
    // Standardize spacing around slashes and hyphens
    .replace(/([0-9]+)\s*\/\s*([0-9]+)/g, "$1/$2")
    // Expand or standardize common abbreviations
    .replace(/\bs[\.\s]*p[\.\s]*/g, "sp ")
    .replace(/\bt[\.\s]*p[\.\s]*/g, "tp ")
    .replace(/\bf[\.\s]*p[\.\s]*/g, "fp ")
    .replace(/\brd\b/g, "road")
    .replace(/\bst\b/g, "street")
    // Remove all punctuation except alphanumeric and forward slashes for plot/flat numbers
    .replace(/[^a-z0-9\/\s]/g, " ")
    // Collapse multiple whitespaces
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * 2. Extract genuine 6-digit Indian PIN code (digits 1-8 followed by 5 digits, not part of a longer phone number)
 */
export function extractPincode(addr: string = ""): string | null {
  if (!addr) return null;
  const match = addr.match(/(?<!\d)[1-8]\d{5}(?!\d)/);
  return match ? match[0] : null;
}

/**
 * 3. Combined Comparator using fuzzball (token_set_ratio)
 * - Accounts for addresses that do not have PIN codes.
 * - Handles landline / mobile numbers (> 6 digits).
 * - Handles subset containment, word reordering, and typos via Levenshtein-based token_set_ratio.
 */
export function areAddressesMatching(
  addr1: string,
  addr2: string,
  threshold = 75
): { isMatch: boolean; score: number; confidence?: string; reason?: string } {
  const pin1 = extractPincode(addr1);
  const pin2 = extractPincode(addr2);

  // If both addresses contain a postal code and they don't match, they are different locations.
  // If either address lacks a postal code, do NOT reject; proceed with fuzzy matching.
  if (pin1 && pin2 && pin1 !== pin2) {
    return { isMatch: false, score: 0, reason: "PIN code mismatch" };
  }

  const norm1 = normalizeAddress(addr1);
  const norm2 = normalizeAddress(addr2);

  if (!norm1 || !norm2) {
    return { isMatch: false, score: 0 };
  }

  const score = fuzz.token_set_ratio(norm1, norm2);

  return {
    isMatch: score >= threshold,
    score,
    confidence: `${score}%`,
  };
}

// Extract delivery address from AWB or Booking
export const getAwbAddress = (awb: any): string => {
  return (
    awb?.address ||
    awb?.consignee_address ||
    awb?._assign_to_booking?.consignee_address ||
    awb?._assign_to_booking?.address ||
    awb?.destination ||
    "GENERAL ROUTE"
  ).trim();
};

export const MotherBagInScanV2: React.FC = () => {
  const navigate = useNavigate();

  // Scan input & audio state
  const [scanInput, setScanInput] = useState<string>("");
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [soundEnabled, setSoundEnabled] = useState<boolean>(true);
  const [lastScannedAwb, setLastScannedAwb] = useState<any | null>(null);

  // Multi-bag state held in frontend
  const [loadedBags, setLoadedBags] = useState<LoadedMotherbag[]>([]);
  const [selectedBagId, setSelectedBagId] = useState<any>(null);

  // DRS groups state held in frontend
  const [drsGroups, setDrsGroups] = useState<Record<string, LocationGroup>>({});

  // Search filter for AWB list
  const [awbSearch, setAwbSearch] = useState<string>("");

  // Configured motherbag addresses and tags from backend
  const [addrTagList, setAddrTagList] = useState<{ addr: string; tag: string }[]>([]);

  // Saving state
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Subbranch & delivery agents
  const [mySubbranch, setMySubbranch] = useState<any>(null);
  const [deliveryAgentOptions, setDeliveryAgentOptions] = useState<any[]>([]);

  // Refs
  const scanInputRef = useRef<HTMLInputElement>(null);
  const loadedBagsRef = useRef<LoadedMotherbag[]>([]);
  loadedBagsRef.current = loadedBags;
  const drsGroupsRef = useRef<Record<string, LocationGroup>>({});
  drsGroupsRef.current = drsGroups;

  // Transport mode visual helper from Mother Bag IN-SCAN
  const getModeSvg = (mode: string) => {
    switch (mode) {
      case "By Train":
        return (
          <span title="By Train" className="inline-flex items-center justify-center p-1 bg-white rounded-full border border-red-200 shadow-xs">
            <svg xmlns="http://www.w3.org/2000/svg" height="22" width="22" viewBox="0 0 24 24">
              <path
                fill="#B8001F"
                d="M12 2c-4 0-8 .5-8 4v9.5C4 17.43 5.57 19 7.5 19L6 20.5v.5h2.23l2-2H14l2 2h2v-.5L16.5 19c1.93 0 3.5-1.57 3.5-3.5V6c0-3.5-3.58-4-8-4zM7.5 17c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm3.5-7H6V6h5v4zm2 0V6h5v4h-5zm3.5 7c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"
              />
            </svg>
          </span>
        );
      case "By Road":
        return (
          <span title="By Road" className="inline-flex items-center justify-center p-1 bg-gray-200 rounded-full border border-gray-300 shadow-xs">
            <svg xmlns="http://www.w3.org/2000/svg" height="22" width="22" viewBox="0 0 48 48">
              <g fill="none" fillRule="evenodd" strokeLinecap="round" strokeLinejoin="round">
                <path d="m0 0h48v48h-48z" fill="#fff" fillOpacity=".01" />
                <g stroke="#000" strokeWidth="4" transform="translate(6 5.091863)">
                  <path d="m5 .90813664-5 35.99999996" />
                  <path d="m36.9287415.90813664-4.9643707 35.95523776" transform="matrix(-1 0 0 1 67.928742 0)" />
                  <path d="m18 .90813664v6" />
                  <path d="m18 29.9081366v7" />
                  <path d="m18 14.9081366v7" />
                </g>
              </g>
            </svg>
          </span>
        );
      case "By Air":
        return (
          <span title="By Air" className="inline-flex items-center justify-center p-1 bg-black-900 rounded-full w-7 h-7 shadow-xs">
            <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" enableBackground="new 0 0 512 512" viewBox="0 0 512 512">
              <path
                fill="#08C2FF"
                d="m490.7 0c-21.3 0-42.7 0-53.3 10.7l-85.3 96h-330.8l-21.3 32 234.7 74.7-64 85.3h-96l-32 32 64 21.3v32c.7 11.5 10.7 21.3 21.3 21.3h32l21.3 64 32-32v-96l85.3-64 74.7 234.7 32-21.3v-330.7l96-85.3c10.7-10.7 10.7-32 10.7-53.4 0-10.6-10.7-21.3-21.3-21.3z"
              />
            </svg>
          </span>
        );
      default:
        return <span className="text-[10px] font-bold text-gray-600 uppercase">{mode || "ROAD"}</span>;
    }
  };

  // Audio feedback synthesizer (Web Audio API)
  const playBeep = (type: "success" | "duplicate" | "error" = "success") => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      if (type === "success") {
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        gain.gain.setValueAtTime(0.15, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.12);
      } else if (type === "duplicate") {
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.08);
        setTimeout(() => {
          try {
            const osc2 = ctx.createOscillator();
            const gain2 = ctx.createGain();
            osc2.connect(gain2);
            gain2.connect(ctx.destination);
            osc2.frequency.setValueAtTime(330, ctx.currentTime);
            gain2.gain.setValueAtTime(0.2, ctx.currentTime);
            gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
            osc2.start(ctx.currentTime);
            osc2.stop(ctx.currentTime + 0.15);
          } catch (e) {}
        }, 90);
      } else {
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(220, ctx.currentTime);
        gain.gain.setValueAtTime(0.2, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.25);
      }
    } catch (e) {}
  };

  // User subbranch
  useEffect(() => {
    try {
      const auth = localStorage.getItem("auth");
      const userD = auth ? JSON.parse(auth) : {};
      if (userD?.state?.user?.subbranch) {
        setMySubbranch(userD.state.user.subbranch);
      }
    } catch (e) {}
  }, []);

  // Preload incoming motherbags for current destination subbranch (like MotherBagInScan.tsx)
  const { data: incomingMbagsRes } = useQuery(
    ["IncomingMotherbagsForInscan", mySubbranch],
    () =>
      ListCommanWay(
        "/admin/mother_bag/list",
        {
          outscanDone: true,
          isInscan: true,
          ...(mySubbranch ? { delivery_subbranch: mySubbranch } : {}),
        },
        { paginate: 5000, include: ["_destination", "_subbranch", "_vendor", "_flight", "_airline"] }
      ),
    {
      enabled: !!mySubbranch,
    }
  );

  const incomingMbagsList: any[] = useMemo(() => {
    return incomingMbagsRes?.data?.data || [];
  }, [incomingMbagsRes]);

  // Fetch Delivery Agents for DRS dropdown
  useQuery(
    ["DeliveryAgentList", mySubbranch],
    () =>
      ListCommanWay(
        "/admin/user/list",
        {
          isActive: true,
          ...(mySubbranch ? { subbranch: mySubbranch } : {}),
        },
        { paginate: 1000 }
      ),
    {
      onSuccess(data) {
        if (data.status === "SUCCESS" && data.data?.data) {
          const opts = data.data.data.map((u: any) => ({
            value: u.id,
            label: u.name ? `${u.name} (${u.empCode || u.username})` : u.username,
          }));
          setDeliveryAgentOptions(opts);
        }
      },
    }
  );

  // Fetch configured motherbag address tags from /admin/mother_bag_addr/list
  useQuery(
    ["MotherBagAddrList"],
    () => ListCommanWay("/admin/mother_bag_addr/list", {}, {}),
    {
      onSuccess(data) {
        if (data?.status === "SUCCESS") {
          const list = Array.isArray(data.data?.data)
            ? data.data.data
            : Array.isArray(data.data)
            ? data.data
            : [];
          setAddrTagList(list);
          if (loadedBagsRef.current.length > 0) {
            updateDrsGroups(loadedBagsRef.current, list);
          }
        }
      },
    }
  );

  // Keep scan input focused
  useEffect(() => {
    scanInputRef.current?.focus();
  }, []);

  // Resolve Tag for an AWB address: if address is in list show TAG, otherwise show full address
  const resolveAddressTag = (
    rawAddr: string,
    knownList: { addr: string; tag: string }[] = addrTagList
  ): { tag: string; isKnown: boolean } => {
    const trimmed = (rawAddr || "").trim();
    if (!trimmed) {
      return { tag: "GENERAL ROUTE", isKnown: false };
    }

    for (const item of knownList) {
      if (item.addr) {
        const matchRes = areAddressesMatching(item.addr, trimmed);
        if (matchRes.isMatch) {
          return { tag: item.tag, isKnown: true };
        }
      }
    }

    // If not in list, show full address instead of that tag
    return { tag: trimmed, isKnown: false };
  };

  // Synchronize DRS Groups whenever in-scanned AWBs change
  const updateDrsGroups = (
    bags: LoadedMotherbag[],
    customTagList?: { addr: string; tag: string }[]
  ) => {
    const tagsList = customTagList || addrTagList;
    setDrsGroups((prevGroups) => {
      const nextGroups: Record<string, LocationGroup> = { ...prevGroups };

      // Collect all currently in-scanned AWBs
      const allInScannedAwbs: any[] = [];
      bags.forEach((bag) => {
        bag.allAwbs.forEach((awb) => {
          if (bag.inScannedAwbNos.includes(awb.awb_no)) {
            allInScannedAwbs.push({ ...awb, mother_bag_code: bag.code, mother_bag_id: bag.id });
          }
        });
      });

      // Group in-scanned AWBs by resolved tag / address
      const clusters: { key: string; tag: string; isKnown: boolean; rawAddress: string; awbs: any[] }[] = [];

      allInScannedAwbs.forEach((awb) => {
        const addr = getAwbAddress(awb);
        const { tag, isKnown } = resolveAddressTag(addr, tagsList);

        let found = clusters.find((c) => {
          if (isKnown && c.isKnown) {
            return c.tag.toUpperCase() === tag.toUpperCase();
          }
          return areAddressesMatching(c.rawAddress, addr).isMatch;
        });

        if (found) {
          found.awbs.push(awb);
        } else {
          const prevKey = Object.keys(nextGroups).find((k) => {
            if (isKnown && nextGroups[k].tag) {
              return nextGroups[k].tag.toUpperCase() === tag.toUpperCase();
            }
            return areAddressesMatching(nextGroups[k].locationName, addr).isMatch;
          });

          clusters.push({
            key: prevKey || (isKnown ? `TAG_${tag}` : normalizeAddress(addr)) || "GENERAL_ROUTE",
            tag: tag,
            isKnown: isKnown,
            rawAddress: addr,
            awbs: [awb],
          });
        }
      });

      const activeKeys: string[] = [];

      clusters.forEach((cluster, idx) => {
        const locKey = cluster.key;
        activeKeys.push(locKey);
        const totalWt = cluster.awbs.reduce((acc, curr) => acc + (Number(curr.weight) || 0), 0);
        const palette = TABLE_PALETTES[idx % TABLE_PALETTES.length];

        if (!nextGroups[locKey]) {
          nextGroups[locKey] = {
            locationKey: locKey,
            locationName: cluster.tag,
            tag: cluster.tag,
            tableTag: cluster.tag,
            palette,
            awbNos: cluster.awbs.map((a) => a.awb_no),
            awbs: cluster.awbs,
            totalWeight: totalWt,
            assignedAgent: null,
            drsGenerated: false,
          };
        } else {
          nextGroups[locKey] = {
            ...nextGroups[locKey],
            locationName: cluster.tag,
            tag: cluster.tag,
            tableTag: cluster.tag,
            awbNos: cluster.awbs.map((a) => a.awb_no),
            awbs: cluster.awbs,
            totalWeight: totalWt,
          };
        }
      });

      Object.keys(nextGroups).forEach((key) => {
        if (!activeKeys.includes(key)) {
          delete nextGroups[key];
        }
      });

      return nextGroups;
    });
  };

  // Process Scanned AWB
  const handleAwbScan = async (rawAwb: string) => {
    const val = rawAwb.trim().toUpperCase();
    if (!val) return;
    setIsScanning(true);

    try {
      // Step A: Check if already loaded in any bag in memory
      let existingBagIndex = -1;
      let existingAwbObj: any = null;

      loadedBagsRef.current.forEach((b, bIdx) => {
        const found = b.allAwbs.find((a) => a.awb_no === val);
        if (found) {
          existingBagIndex = bIdx;
          existingAwbObj = found;
        }
      });

      if (existingBagIndex !== -1 && existingAwbObj) {
        const targetBag = loadedBagsRef.current[existingBagIndex];
        const isAlreadyInScanned = targetBag.inScannedAwbNos.includes(val);

        if (isAlreadyInScanned) {
          playBeep("duplicate");
          UseToast(`AWB ${val} already In-Scanned!`, "error");
          setLastScannedAwb(existingAwbObj);
          setSelectedBagId(targetBag.id);
          setIsScanning(false);
          setScanInput("");
          scanInputRef.current?.focus();
          return;
        }

        const updatedBags = [...loadedBagsRef.current];
        updatedBags[existingBagIndex] = {
          ...targetBag,
          inScannedAwbNos: [...targetBag.inScannedAwbNos, val],
          shortAwbNos: targetBag.shortAwbNos.filter((x) => x !== val),
        };

        playBeep("success");
        setLoadedBags(updatedBags);
        setSelectedBagId(targetBag.id);
        setLastScannedAwb(existingAwbObj);
        updateDrsGroups(updatedBags);
        setScanInput("");
        setIsScanning(false);
        scanInputRef.current?.focus();
        return;
      }

      // Step B: Not in memory, fetch AWB from backend (without _destination in includes)
      let awbRecord: any = null;
      const awbListRes: any = await ListCommanWay(
        "/admin/awbdata/list",
        { awb_no: val },
        {
          include: ["_assign_to_booking", "_mother_bag_id", "_subbranch"],
          paginate: 5,
        }
      );

      if (awbListRes?.status === "SUCCESS" && awbListRes.data?.data?.length > 0) {
        awbRecord = awbListRes.data.data[0];
      } else {
        try {
          const podRes: any = await GetCommanWay("/admin/proof_of_delivery/awb_details/" + val, {}, {});
          if (podRes?.status === "SUCCESS" && podRes.data?.awb_details) {
            awbRecord = {
              ...podRes.data.awb_details,
              _assign_to_booking: podRes.data.booking_details,
            };
          }
        } catch (e) {}
      }

      if (!awbRecord) {
        playBeep("error");
        UseToast(`AWB "${val}" not found!`, "error");
        setIsScanning(false);
        setScanInput("");
        scanInputRef.current?.focus();
        return;
      }

      // Step C: Parent Motherbag lookup (find actual motherbag code and id)
      const rawBagId =
        awbRecord.mother_bag_id ||
        awbRecord._mother_bag_id?.id ||
        awbRecord._mother_bag_id;

      const directBagCode =
        awbRecord.mother_bag_code ||
        awbRecord._mother_bag_id?.mother_bag_code ||
        awbRecord._mother_bag_id?.code;

      if (!rawBagId && !directBagCode) {
        // Loose parcel without a motherbag
        const pseudoBagId = "loose_parcels";
        let pseudoBag = loadedBagsRef.current.find((b) => b.id === pseudoBagId);
        let updatedBags: LoadedMotherbag[];

        if (!pseudoBag) {
          pseudoBag = {
            id: pseudoBagId,
            code: "UNASSIGNED PARCELS",
            bagData: {
              mother_bag_code: "UNASSIGNED PARCELS",
              totalNos: 1,
              status: "IN PROGRESS",
              _subbranch: { branch_name: "Direct Arrival" },
            },
            allAwbs: [awbRecord],
            inScannedAwbNos: [val],
            shortAwbNos: [],
          };
          updatedBags = [...loadedBagsRef.current, pseudoBag];
        } else {
          updatedBags = loadedBagsRef.current.map((b) =>
            b.id === pseudoBagId
              ? {
                  ...b,
                  allAwbs: [...b.allAwbs, awbRecord],
                  inScannedAwbNos: [...b.inScannedAwbNos, val],
                }
              : b
          );
        }

        playBeep("success");
        setLoadedBags(updatedBags);
        setSelectedBagId(pseudoBagId);
        setLastScannedAwb(awbRecord);
        updateDrsGroups(updatedBags);
        UseToast(`AWB ${val} in-scanned (loose parcel)`, "success");
        setIsScanning(false);
        setScanInput("");
        scanInputRef.current?.focus();
        return;
      }

      // 1. Check if motherbag is already loaded in current session
      const existingBag = loadedBagsRef.current.find(
        (b) =>
          (rawBagId && String(b.id) === String(rawBagId)) ||
          (directBagCode && b.code === directBagCode) ||
          (rawBagId && b.code === String(rawBagId))
      );

      if (existingBag) {
        const isPresent = existingBag.allAwbs.some((a) => a.awb_no === val);
        const updatedAwbs = isPresent ? existingBag.allAwbs : [...existingBag.allAwbs, awbRecord];
        const updatedInscans = existingBag.inScannedAwbNos.includes(val)
          ? existingBag.inScannedAwbNos
          : [...existingBag.inScannedAwbNos, val];

        const updatedBags = loadedBagsRef.current.map((b) =>
          b.id === existingBag.id
            ? { ...b, allAwbs: updatedAwbs, inScannedAwbNos: updatedInscans }
            : b
        );

        playBeep("success");
        setLoadedBags(updatedBags);
        setSelectedBagId(existingBag.id);
        setLastScannedAwb(awbRecord);
        updateDrsGroups(updatedBags);
      } else {
        // Step D: Find Motherbag details & sibling AWBs
        let mbagData: any = null;

        // Try preloaded list first
        if (incomingMbagsList.length > 0) {
          mbagData = incomingMbagsList.find(
            (m: any) =>
              (directBagCode && m.mother_bag_code === directBagCode) ||
              (rawBagId && m.id === Number(rawBagId)) ||
              (rawBagId && String(m.id) === String(rawBagId)) ||
              (rawBagId && m.mother_bag_code === String(rawBagId))
          );
        }

        // Fallback: Query mother_bag/list from backend
        if (!mbagData && (rawBagId || directBagCode)) {
          try {
            const queryPayload: any = {};
            if (directBagCode) {
              queryPayload.mother_bag_code = directBagCode;
            } else if (rawBagId && !isNaN(Number(rawBagId))) {
              queryPayload.id = Number(rawBagId);
            } else if (rawBagId) {
              queryPayload.mother_bag_code = rawBagId;
            }

            const mbagRes: any = await ListCommanWay(
              "/admin/mother_bag/list",
              queryPayload,
              {
                include: ["_destination", "_subbranch", "_vendor", "_flight", "_airline"],
                paginate: 1,
              }
            );

            if (mbagRes?.status === "SUCCESS" && mbagRes.data?.data?.[0]) {
              mbagData = mbagRes.data.data[0];
            }
          } catch (e) {
            console.error("Motherbag query error:", e);
          }
        }

        // Determine real motherbag code and id
        const finalCode =
          mbagData?.mother_bag_code ||
          directBagCode ||
          awbRecord.mother_bag_code ||
          awbRecord._mother_bag_id?.mother_bag_code ||
          (rawBagId ? `MBAG-${rawBagId}` : "MBAG-UNKNOWN");

        const finalId = mbagData?.id || rawBagId || finalCode;

        // Query sibling AWBs for this motherbag
        const siblingsRes: any = await ListCommanWay(
          "/admin/awbdata/list",
          { mother_bag_id: finalId, isInscan: true },
          {
            include: ["_assign_to_booking", "_subbranch"],
            paginate: 1000,
          }
        );

        let siblingAwbs = siblingsRes?.status === "SUCCESS" && siblingsRes.data?.data ? siblingsRes.data.data : [];

        if (!siblingAwbs.some((a: any) => a.awb_no === val)) {
          siblingAwbs = [awbRecord, ...siblingAwbs];
        }

        const newBagEntry: LoadedMotherbag = {
          id: finalId,
          code: finalCode,
          bagData: mbagData || {
            id: finalId,
            mother_bag_code: finalCode,
            totalNos: siblingAwbs.length,
            status: "IN PROGRESS",
            modeOfPkg: "By Road",
            _subbranch: awbRecord._subbranch,
            _destination: { city_name: awbRecord.destination },
          },
          allAwbs: siblingAwbs,
          inScannedAwbNos: [val],
          shortAwbNos: [],
        };

        const updatedBags = [...loadedBagsRef.current, newBagEntry];
        playBeep("success");
        setLoadedBags(updatedBags);
        setSelectedBagId(newBagEntry.id);
        setLastScannedAwb(awbRecord);
        updateDrsGroups(updatedBags);
        UseToast(`Loaded Motherbag: ${finalCode}`, "success");
      }
    } catch (err: any) {
      playBeep("error");
      console.error("Scan error:", err);
      UseToast("Error scanning AWB.", "error");
    } finally {
      setIsScanning(false);
      setScanInput("");
      scanInputRef.current?.focus();
    }
  };

  // Global Session Tally stats
  const globalStats = useMemo(() => {
    let totalAwbs = 0;
    let totalInScanned = 0;
    loadedBags.forEach((b) => {
      totalAwbs += b.allAwbs.length;
      totalInScanned += b.inScannedAwbNos.length;
    });
    return {
      bagsCount: loadedBags.length,
      totalAwbs,
      totalInScanned,
      totalPending: Math.max(0, totalAwbs - totalInScanned),
    };
  }, [loadedBags]);

  // Unified AWB List: All AWBs, with In-Scanned on TOP and Pending at the BOTTOM
  const unifiedAwbsList = useMemo(() => {
    let list: { awb: any; bagCode: string; bagId: any; isInScanned: boolean }[] = [];

    const bags = selectedBagId
      ? loadedBags.filter((b) => b.id === selectedBagId)
      : loadedBags;

    bags.forEach((bag) => {
      bag.allAwbs.forEach((awb) => {
        const isInScanned = bag.inScannedAwbNos.includes(awb.awb_no);
        list.push({
          awb,
          bagCode: bag.code,
          bagId: bag.id,
          isInScanned,
        });
      });
    });

    if (awbSearch.trim()) {
      const q = awbSearch.trim().toLowerCase();
      list = list.filter(
        (x) =>
          x.awb.awb_no?.toLowerCase().includes(q) ||
          x.awb.consignee?.toLowerCase().includes(q) ||
          x.awb.destination?.toLowerCase().includes(q) ||
          x.awb.address?.toLowerCase().includes(q) ||
          x.bagCode?.toLowerCase().includes(q)
      );
    }

    // Sort: Scanned AWBs on TOP (isInScanned = true first), Unscanned at BOTTOM
    list.sort((a, b) => {
      if (a.isInScanned && !b.isInScanned) return -1;
      if (!a.isInScanned && b.isInScanned) return 1;
      return 0;
    });

    return list;
  }, [loadedBags, selectedBagId, awbSearch]);

  // 1-Click DRS Staging
  const handleStageDRS = (locationKey: string) => {
    const group = drsGroups[locationKey];
    if (!group) return;

    if (!group.assignedAgent) {
      UseToast("Please select a Delivery Agent first!", "error");
      return;
    }

    if (group.awbNos.length === 0) {
      UseToast("No in-scanned parcels in this location group", "error");
      return;
    }

    setDrsGroups((prev) => ({
      ...prev,
      [locationKey]: {
        ...prev[locationKey],
        drsGenerated: true,
      },
    }));

    UseToast(`DRS staged for ${group.locationName} (${group.awbNos.length} parcels assigned to ${group.assignedAgent.label})`, "success");
  };

  const handleUndoStageDRS = (locationKey: string) => {
    setDrsGroups((prev) => ({
      ...prev,
      [locationKey]: {
        ...prev[locationKey],
        drsGenerated: false,
      },
    }));
  };

  // Final Save Action: Commit In-Scans & Generate DRS records
  const handleFinalSave = async () => {
    if (loadedBags.length === 0) {
      UseToast("No motherbags or parcels scanned yet!", "error");
      return;
    }

    if (globalStats.totalInScanned === 0) {
      UseToast("Please in-scan at least 1 AWB before saving", "error");
      return;
    }

    const bagsWithMissingAwbs: { bag: LoadedMotherbag; missingCount: number; missingAwbs: string[] }[] = [];
    for (const bag of loadedBags) {
      if (bag.id === "loose_parcels") continue;
      const missing = bag.allAwbs
        .filter((a) => !bag.inScannedAwbNos.includes(a.awb_no))
        .map((a) => a.awb_no);
      if (missing.length > 0) {
        bagsWithMissingAwbs.push({ bag, missingCount: missing.length, missingAwbs: missing });
      }
    }

    const stagedDrsList = Object.values(drsGroups).filter((g) => g.drsGenerated && g.assignedAgent);

    let confirmText = `Save In-Scan for ${loadedBags.length} Motherbag(s) (${globalStats.totalInScanned} AWBs).`;

    if (stagedDrsList.length > 0) {
      confirmText += `\n\n${stagedDrsList.length} DRS will be generated.`;
    }

    if (bagsWithMissingAwbs.length > 0) {
      const missingSummary = bagsWithMissingAwbs
        .map((b) => `${b.bag.code}: ${b.missingCount} missing (${b.missingAwbs.slice(0, 3).join(", ")}${b.missingAwbs.length > 3 ? "..." : ""})`)
        .join("\n");
      confirmText += `\n\nShort Pieces Alert:\n${missingSummary}\n\nUnscanned parcels will be declared as SHORT.`;
    }

    const result = await Swal.fire({
      title: "Save In-Scan",
      text: confirmText,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#16a34a",
    });

    if (!result.isConfirmed) return;

    setIsSaving(true);
    const generatedDrsResults: { code: string; location: string; agent: string; count: number }[] = [];

    try {
      // 1. Update each Motherbag in backend
      for (const bag of loadedBags) {
        if (bag.id === "loose_parcels") continue;
        const missing = bag.allAwbs
          .filter((a) => !bag.inScannedAwbNos.includes(a.awb_no))
          .map((a) => a.awb_no);

        await UpdateCommanWay({
          url: "/admin/mother_bag/update/" + bag.id,
          data: {
            inScanPOD: true,
            inscanDone: true,
            selectedAwbs: bag.inScannedAwbNos,
            shortAwbs: missing.length ? missing : undefined,
            status: "RECEIVED",
          },
        });
      }

      // 2. Generate DRS for staged groups
      for (const group of stagedDrsList) {
        try {
          const drsRes: any = await CreateCommanWay({
            url: "/admin/drs/add",
            data: {
              awb_no: group.awbNos,
              delivery_agent: group.assignedAgent?.value,
              agent_id: group.assignedAgent?.value,
              assign_to_delivery_agent: group.assignedAgent?.value,
            },
          });

          if (drsRes?.status === "SUCCESS") {
            const drsCode = drsRes.data?.code || `DRS-${group.locationName}`;
            generatedDrsResults.push({
              code: drsCode,
              location: group.locationName,
              agent: group.assignedAgent?.label || "Assigned",
              count: group.awbNos.length,
            });
          }
        } catch (drsErr) {
          console.error("DRS generation error:", drsErr);
        }
      }

      // Success modal
      let successHtml = `<div class="text-left space-y-2">
        <p><strong>${loadedBags.length} Motherbag(s)</strong> In-Scanned (${globalStats.totalInScanned} AWBs)</p>`;

      if (generatedDrsResults.length > 0) {
        successHtml += `<div class="bg-gray-50 border rounded p-2 text-sm">
          <strong>Generated DRS:</strong>
          <ul>
            ${generatedDrsResults
              .map((d) => `<li>• <strong>${d.code}</strong> (${d.location}) → ${d.agent} (${d.count} AWBs)</li>`)
              .join("")}
          </ul>
        </div>`;
      }
      successHtml += `</div>`;

      await Swal.fire({
        title: "Saved Successfully",
        html: successHtml,
        icon: "success",
        confirmButtonText: "Go to DRS List",
        showCancelButton: true,
        cancelButtonText: "New In-Scan",
      }).then((resp) => {
        if (resp.isConfirmed) {
          navigate("/drs/list");
        } else {
          setLoadedBags([]);
          setSelectedBagId(null);
          setDrsGroups({});
          setLastScannedAwb(null);
          scanInputRef.current?.focus();
        }
      });
    } catch (err: any) {
      console.error("Save error:", err);
      Swal.fire({
        title: "Save Error",
        text: typeof err === "string" ? err : err.message || "Failed to save. Try again.",
        icon: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Active parcel's location info and palette
  const activeParcelAddr = lastScannedAwb ? getAwbAddress(lastScannedAwb) : "";
  const activeParcelGroup = activeParcelAddr
    ? Object.values(drsGroups).find((g) =>
        areAddressesMatching(g.locationName, activeParcelAddr) ||
        areAddressesMatching(g.locationKey, normalizeAddress(activeParcelAddr))
      )
    : null;
  const activeParcelPalette = activeParcelGroup?.palette || TABLE_PALETTES[0];

  return (
    <div className="min-h-screen bg-gray-100 text-gray-900 flex flex-col font-sans pb-16">
      {/* ═══════════════════════════════════════════════════════════════════
          HEADER: AWB SCAN INPUT + SAVE BUTTON
      ═══════════════════════════════════════════════════════════════════ */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-300 shadow-sm">
        <div className="w-full px-4 py-2.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 whitespace-nowrap">
              Mother Bag In-Scan V2
            </h1>
          </div>

          {/* Top Scan Bar */}
          <div className="flex-1 max-w-2xl flex items-center gap-2">
            <div className="relative flex-1">
              <input
                ref={scanInputRef}
                type="text"
                value={scanInput}
                onChange={(e) => setScanInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    handleAwbScan(scanInput);
                  }
                }}
                disabled={isScanning}
                placeholder="Scan AWB barcode..."
                className="w-full px-3 py-2 text-sm font-semibold rounded-md border-2 border-blue-400 bg-blue-50/50 focus:bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-all"
                autoFocus
              />
            </div>

            <button
              type="button"
              onClick={() => handleAwbScan(scanInput)}
              disabled={isScanning || !scanInput.trim()}
              className="px-4 py-2 rounded-md text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 active:scale-95 disabled:opacity-50 whitespace-nowrap transition-all shadow-sm"
            >
              {isScanning ? "Scanning..." : "Scan AWB"}
            </button>

            <button
              type="button"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? "Mute sound" : "Enable sound"}
              className={`p-2 rounded-md border text-xs ${
                soundEnabled ? "bg-green-50 border-green-300 text-green-700" : "bg-gray-100 text-gray-400"
              }`}
            >
              {soundEnabled ? <SpeakerWaveIcon className="w-4 h-4" /> : <SpeakerXMarkIcon className="w-4 h-4" />}
            </button>
          </div>

          {/* Master Save Button on Top */}
          <div>
            <button
              type="button"
              onClick={handleFinalSave}
              disabled={isSaving || loadedBags.length === 0}
              className="flex items-center gap-1.5 px-5 py-2 rounded-md font-bold text-sm text-white bg-green-600 hover:bg-green-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow transition-all whitespace-nowrap"
            >
              {isSaving ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <ShieldCheckIcon className="w-4 h-4" />
                  <span>SAVE</span>
                  {globalStats.totalInScanned > 0 && (
                    <span className="ml-1 px-1.5 py-0.2 rounded bg-white text-green-800 text-xs font-black">
                      {globalStats.totalInScanned}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════════
          MAIN CONTENT AREA (BY DEFAULT BLANK, POPULATES ON SCAN)
      ═══════════════════════════════════════════════════════════════════ */}
      <main className="w-full px-4 py-3 flex-1">
        {loadedBags.length === 0 ? (
          /* Blank state */
          <div className="h-[60vh] flex items-center justify-center text-gray-400 text-sm">
            Scan an AWB barcode to start In-Scan
          </div>
        ) : (
          /* Main 2-Column Desktop Layout (Left: Motherbags List, Right: Parcel & AWBs) */
          <div className="flex flex-row items-start gap-4 w-full">
            {/* ─────────────────────────────────────────────────────────────
                LEFT PANEL: MOTHERBAGS LIST (many bags appear as user scans)
                Showing full details from Mother Bag IN-SCAN
            ───────────────────────────────────────────────────────────── */}
            <div className="w-[380px] shrink-0 bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden flex flex-col">
              <div className="px-3 py-2 bg-gray-900 text-white flex items-center justify-between text-xs font-bold">
                <span className="flex items-center gap-1.5">
                  <InboxStackIcon className="w-4 h-4 text-blue-400" />
                  Motherbags ({loadedBags.length})
                </span>
                <span className="text-gray-300 font-mono">
                  {globalStats.totalInScanned} / {globalStats.totalAwbs} Scanned
                </span>
              </div>

              {/* Motherbag Cards Scrollable List */}
              <div className="divide-y divide-gray-200 max-h-[calc(100vh-160px)] overflow-y-auto p-1.5 space-y-1.5">
                {loadedBags.map((b) => {
                  const isSelected = selectedBagId === b.id;
                  const total = b.allAwbs.length;
                  const scanned = b.inScannedAwbNos.length;
                  const pct = total > 0 ? Math.round((scanned / total) * 100) : 0;
                  const isDone = scanned === total && total > 0;
                  const bag = b.bagData || {};

                  // Status styling matching Mother Bag IN-SCAN
                  const statusBg =
                    bag.status === "SHORT"
                      ? "bg-teal-600 text-white"
                      : isDone || bag.inscanDone
                      ? "bg-green-600 text-white"
                      : isSelected
                      ? "bg-blue-600 text-white"
                      : "bg-red-500 text-white";

                  return (
                    <div
                      key={b.id}
                      onClick={() => setSelectedBagId(isSelected ? null : b.id)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? "border-blue-600 ring-2 ring-blue-300 bg-blue-50/60 shadow-sm"
                          : isDone
                          ? "border-green-300 bg-green-50/30 hover:bg-green-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                      }`}
                    >
                      {/* Top Row: Motherbag Code (Prominent), ID & Mode SVG */}
                      <div className="flex items-center justify-between gap-2 border-b border-gray-100 pb-1.5 mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-base text-gray-900 font-mono tracking-tight hover:underline">
                            {bag.mother_bag_code || b.code}
                          </span>
                          <span className="text-[10px] text-gray-400 font-mono">
                            #{bag.id || b.id}
                          </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                          {getModeSvg(bag.modeOfPkg)}
                          <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${statusBg}`}>
                            {bag.status || (isDone ? "RECEIVED" : "IN SCANNING")}
                          </span>
                        </div>
                      </div>

                      {/* Origin ➔ Destination */}
                      <div className="flex items-center justify-between text-xs text-gray-700 font-semibold mb-1">
                        <span className="truncate max-w-[140px]" title={bag._subbranch?.branch_name}>
                          {bag._subbranch?.branch_name || "Origin"}
                        </span>
                        <span className="text-gray-400 px-1 font-normal">➔</span>
                        <span className="truncate max-w-[140px]" title={bag._destination?.city_name}>
                          {bag._destination?.city_name || "Destination"}
                        </span>
                      </div>

                      {/* Date & Time, Total Nos & Weight */}
                      <div className="grid grid-cols-2 gap-1 text-[11px] text-gray-600 mt-1">
                        <div>
                          <strong>Date: </strong>
                          {bag.createdAt
                            ? `${moment(bag.createdAt, "DD-MM-YYYY HH:mm").format("DD/MM/YYYY")} ${moment(bag.createdAt, "DD-MM-YYYY HH:mm").format("HH:mm")}`
                            : moment().format("DD/MM/YYYY")}
                        </div>
                        <div className="text-right">
                          <strong>Total: </strong>
                          <span className="font-bold text-gray-900">{total}</span> pcs
                          {bag.realWeight ? ` (${bag.realWeight} Kg)` : ""}
                        </div>
                      </div>

                      {/* Tally Progress Bar */}
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-[11px] font-bold mb-0.5">
                          <span className="text-gray-600">In-Scan Tally:</span>
                          <span className={isDone ? "text-green-700" : "text-blue-700"}>
                            {scanned} / {total} ({pct}%)
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${isDone ? "bg-green-600" : "bg-blue-600"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>

                      {/* Additional Details from Mother Bag IN-SCAN (Tag ID, Remark, Vendor, Flight, Slip) */}
                      {(bag.refNo || bag.remark || bag._vendor || bag._flight || bag.VendorSlips?.length) && (
                        <div className="mt-2 pt-1.5 border-t border-gray-100 text-[10px] text-gray-500 space-y-0.5">
                          {bag.refNo && (
                            <div>
                              <strong className="text-gray-700">Tag ID: </strong>
                              {bag.refNo}
                            </div>
                          )}
                          {bag.remark && (
                            <div className="truncate">
                              <strong className="text-gray-700">Remark: </strong>
                              {bag.remark}
                            </div>
                          )}
                          {(bag._vendor?.vendor_name || bag._flight?.name || bag._airline?.name) && (
                            <div className="truncate">
                              <strong className="text-gray-700">Carrier: </strong>
                              {[bag._vendor?.vendor_name, bag._airline?.name, bag._flight?.name].filter(Boolean).join(" &middot; ")}
                            </div>
                          )}
                          {bag.VendorSlips?.length ? (
                            <div className="pt-0.5">
                              <a
                                href={get_file_url(bag.VendorSlips[bag.VendorSlips.length - 1].slip_url)}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-800 text-white rounded text-[10px] font-bold hover:bg-gray-700"
                              >
                                <span>Vendor Slip</span>
                                <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                              </a>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedBagId && (
                <div className="p-2 border-t border-gray-200 bg-gray-50 text-center">
                  <button
                    type="button"
                    onClick={() => setSelectedBagId(null)}
                    className="text-xs text-blue-600 font-semibold hover:underline"
                  >
                    Clear Filter (Show All Bags)
                  </button>
                </div>
              )}
            </div>

            {/* ─────────────────────────────────────────────────────────────
                RIGHT PANEL:
                1. CURRENT SCANNED PARCEL / SORTING TABLE GUIDE (ON TOP)
                2. LOCATION-BASED DRS GENERATION
                3. UNIFIED AWB LIST (SCANNED ON TOP, UNSCANNED AT BOTTOM)
            ───────────────────────────────────────────────────────────── */}
            <div className="flex-1 flex flex-col gap-3 min-w-0">
              {/* CURRENT SCANNED PARCEL / DESTINATION TAG GUIDE */}
              {lastScannedAwb && (() => {
                const activeAddr = getAwbAddress(lastScannedAwb);
                const activeTagInfo = resolveAddressTag(activeAddr);

                return (
                  <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      {/* Prominent TAG Box */}
                      <div
                        className={`px-5 py-3 rounded-lg text-center shadow font-black tracking-tight min-w-[150px] ${
                          activeTagInfo.isKnown
                            ? "bg-blue-600 text-white"
                            : "bg-gray-800 text-white"
                        }`}
                      >
                        <span className="text-[10px] uppercase tracking-wider block opacity-80">
                          {activeTagInfo.isKnown ? "DESTINATION TAG" : "DELIVERY ADDRESS"}
                        </span>
                        <span
                          className={`${
                            activeTagInfo.isKnown
                              ? "text-2xl"
                              : "text-xs font-semibold max-w-xs line-clamp-2"
                          } uppercase block font-extrabold tracking-wide mt-0.5`}
                        >
                          {activeTagInfo.tag}
                        </span>
                      </div>

                      {/* Parcel Details */}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 font-semibold">AWB:</span>
                          <span className="text-lg font-bold font-mono text-gray-900">
                            {lastScannedAwb.awb_no}
                          </span>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold">
                            In-Scanned ✅
                          </span>
                        </div>

                        <div className="text-xs text-gray-600 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
                          <span>
                            <strong>Consignee:</strong> {lastScannedAwb.consignee || "—"}
                          </span>
                          <span>
                            <strong>Destination:</strong> {lastScannedAwb.destination || "—"}
                          </span>
                          <span>
                            <strong>Weight:</strong> {lastScannedAwb.weight ? `${lastScannedAwb.weight} Kg` : "—"}
                          </span>
                        </div>

                        {activeAddr && (
                          <div className="text-[11px] text-gray-500 mt-1 max-w-xl truncate" title={activeAddr}>
                            <strong>Address:</strong> {activeAddr}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Barcode Graphic */}
                    <div className="hidden sm:block">
                      <Barcode
                        value={lastScannedAwb.awb_no || "000000"}
                        width={1.4}
                        height={32}
                        fontSize={11}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* LOCATION-BASED DRS GENERATION */}
              {Object.keys(drsGroups).length > 0 && (
                <div className="bg-white rounded-lg border border-gray-300 p-3 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-gray-800 uppercase tracking-wide flex items-center gap-1.5">
                      <TruckIcon className="w-4 h-4 text-blue-600" />
                      Location DRS Routing ({Object.keys(drsGroups).length} Groups)
                    </span>
                    <span className="text-xs text-gray-500">
                      Select delivery agent per location, creates DRS on Save
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {Object.values(drsGroups).map((group) => (
                      <div
                        key={group.locationKey}
                        className={`p-2.5 rounded-md border text-xs flex flex-col justify-between gap-2 ${
                          group.drsGenerated
                            ? "bg-green-50 border-green-300"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0 pr-2">
                            <span className="font-extrabold text-gray-900 text-xs block truncate" title={group.tag}>
                              {group.tag}
                            </span>
                          </div>
                          <span className="font-bold text-gray-700 whitespace-nowrap text-xs">
                            {group.awbNos.length} pcs &middot; {group.totalWeight.toFixed(1)} Kg
                          </span>
                        </div>

                        {group.drsGenerated ? (
                          <div className="flex items-center justify-between bg-green-100 text-green-900 px-2 py-1 rounded text-xs font-semibold">
                            <span>DRS Assigned: {group.assignedAgent?.label}</span>
                            <button
                              type="button"
                              onClick={() => handleUndoStageDRS(group.locationKey)}
                              className="text-[11px] text-green-700 underline"
                            >
                              Change
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <Select
                                options={deliveryAgentOptions}
                                value={group.assignedAgent}
                                onChange={(val: any) => {
                                  setDrsGroups((prev) => ({
                                    ...prev,
                                    [group.locationKey]: {
                                      ...prev[group.locationKey],
                                      assignedAgent: val,
                                    },
                                  }));
                                }}
                                placeholder="Select Delivery Agent..."
                                className="text-xs"
                                isClearable
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                  control: (base) => ({ ...base, minHeight: "30px", fontSize: "12px" }),
                                }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handleStageDRS(group.locationKey)}
                              className="px-2.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded whitespace-nowrap shadow-sm"
                            >
                              Generate DRS
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ─────────────────────────────────────────────────────────────
                  UNIFIED AWB LIST (SCANNED ON TOP, UNSCANNED AT BOTTOM)
              ───────────────────────────────────────────────────────────── */}
              <div className="bg-white rounded-lg border border-gray-300 shadow-sm overflow-hidden flex flex-col">
                {/* List Header & Search */}
                <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3 text-xs">
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-gray-800">
                      AWB List ({unifiedAwbsList.length})
                    </span>
                    <span className="text-green-700 font-bold">
                      Scanned: {unifiedAwbsList.filter((x) => x.isInScanned).length}
                    </span>
                    <span className="text-amber-700 font-bold">
                      Pending: {unifiedAwbsList.filter((x) => !x.isInScanned).length}
                    </span>
                  </div>

                  <div className="relative">
                    <MagnifyingGlassIcon className="w-3.5 h-3.5 absolute left-2 top-2 text-gray-400" />
                    <input
                      type="text"
                      value={awbSearch}
                      onChange={(e) => setAwbSearch(e.target.value)}
                      placeholder="Search AWB or consignee..."
                      className="pl-7 pr-2 py-1 text-xs rounded border border-gray-300 focus:outline-none focus:border-blue-500 w-52"
                    />
                  </div>
                </div>

                {/* Table: Scanned on TOP, Unscanned on BOTTOM */}
                <div className="overflow-x-auto max-h-[calc(100vh-340px)] overflow-y-auto">
                  <table className="w-full text-left text-xs text-gray-800">
                    <thead className="sticky top-0 bg-gray-800 text-white uppercase text-[11px] font-bold">
                      <tr>
                        <th className="px-3 py-2 w-12 text-center">#</th>
                        <th className="px-3 py-2 w-28">Status</th>
                        <th className="px-3 py-2">AWB No</th>
                        <th className="px-3 py-2">Motherbag</th>
                        <th className="px-3 py-2">Consignee</th>
                        <th className="px-3 py-2">Tag / Address</th>
                        <th className="px-3 py-2 text-right">Weight</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {unifiedAwbsList.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-6 text-gray-400 text-xs">
                            No AWBs found.
                          </td>
                        </tr>
                      ) : (
                        unifiedAwbsList.map(({ awb, bagCode, isInScanned }, idx) => {
                          const awbAddr = getAwbAddress(awb);
                          const group = Object.values(drsGroups).find((g) =>
                            areAddressesMatching(g.locationName, awbAddr) ||
                            areAddressesMatching(g.locationKey, normalizeAddress(awbAddr))
                          );
                          const isCurrent = lastScannedAwb?.awb_no === awb.awb_no;

                          return (
                            <tr
                              key={awb.awb_no + "-" + idx}
                              onClick={() => setLastScannedAwb(awb)}
                              className={`cursor-pointer transition-colors ${
                                isCurrent
                                  ? "bg-blue-100 font-semibold"
                                  : isInScanned
                                  ? "bg-green-100 hover:bg-green-200 text-gray-900"
                                  : "bg-white hover:bg-gray-50 text-gray-700"
                              }`}
                            >
                              <td className="px-3 py-2 text-center font-bold text-gray-500">
                                {idx + 1}
                              </td>

                              <td className="px-3 py-2 whitespace-nowrap">
                                {isInScanned ? (
                                  <span className="inline-flex items-center gap-1 font-bold text-green-800 bg-green-200 px-2 py-0.5 rounded text-[11px]">
                                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-700" />
                                    Scanned
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded text-[11px]">
                                    <ClockIcon className="w-3.5 h-3.5 text-amber-700" />
                                    Pending
                                  </span>
                                )}
                              </td>

                              <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">
                                {awb.awb_no}
                              </td>

                              <td className="px-3 py-2 font-medium whitespace-nowrap text-gray-600">
                                {bagCode}
                              </td>

                              <td className="px-3 py-2 max-w-[160px] truncate" title={awb.consignee}>
                                {awb.consignee || "—"}
                              </td>

                              <td className="px-3 py-2 max-w-[320px]">
                                {(() => {
                                  const tagInfo = resolveAddressTag(awbAddr);
                                  if (tagInfo.isKnown) {
                                    return (
                                      <div>
                                        <span className="inline-block px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-900 border border-blue-200">
                                          {tagInfo.tag}
                                        </span>
                                        <div className="text-[10px] text-gray-500 truncate mt-0.5" title={awbAddr}>
                                          {awbAddr}
                                        </div>
                                      </div>
                                    );
                                  }
                                  return (
                                    <div className="text-xs text-gray-800 font-medium truncate" title={awbAddr}>
                                      {awbAddr}
                                    </div>
                                  );
                                })()}
                              </td>

                              <td className="px-3 py-2 text-right whitespace-nowrap font-medium">
                                {awb.weight ? `${awb.weight} Kg` : "—"}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* ═══════════════════════════════════════════════════════════════════
          STICKY BOTTOM SAVE BAR
      ═══════════════════════════════════════════════════════════════════ */}
      {loadedBags.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-30 bg-gray-900 text-white border-t border-gray-800 py-2.5 px-4 shadow-lg">
          <div className="w-full flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-6">
              <span>
                Bags: <strong className="text-white text-sm">{loadedBags.length}</strong>
              </span>
              <span>
                Scanned: <strong className="text-green-400 text-sm">{globalStats.totalInScanned}</strong> / {globalStats.totalAwbs}
              </span>
              <span>
                Pending: <strong className="text-amber-400 text-sm">{globalStats.totalPending}</strong>
              </span>
              <span>
                DRS Configured: <strong className="text-blue-400 text-sm">{Object.values(drsGroups).filter((g) => g.drsGenerated).length}</strong>
              </span>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-gray-400 text-[11px] hidden sm:inline">
                All changes held in memory until Save
              </span>
              <button
                type="button"
                onClick={handleFinalSave}
                disabled={isSaving || globalStats.totalInScanned === 0}
                className="flex items-center gap-1 px-5 py-2 rounded font-bold text-sm text-white bg-green-600 hover:bg-green-700 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow transition-all"
              >
                {isSaving ? "Saving..." : "SAVE ALL"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MotherBagInScanV2;
